import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useCurrentUser } from "../context/AuthContext";
import { nameFor } from "../utils/displayName";

const GET_BOOKING_FOR_REVIEW = gql`
  query BookingForReview($id: Int!) {
    bookingById(id: $id) {
      id endAt status
      saunaBySaunaId {
        id name
        userByOwnerId { id userProfileByUserId { fullName displayName } }
      }
      userByRenterId { id userProfileByUserId { fullName displayName } }
      reviewsByBookingId {
        nodes { id reviewerId reviewerRole }
      }
    }
  }
`;

const SUBMIT_REVIEW = gql`
  mutation SubmitReviewWizard(
    $bookingId: Int!
    $overall: Int!
    $cleanliness: Int
    $communication: Int
    $accuracy: Int
    $valueScore: Int
    $respectRules: Int
    $body: String
    $privateFeedback: String
  ) {
    submitReview(input: {
      bookingId: $bookingId
      overall: $overall
      cleanliness: $cleanliness
      communication: $communication
      accuracy: $accuracy
      valueScore: $valueScore
      respectRules: $respectRules
      body: $body
      privateFeedback: $privateFeedback
    }) {
      review { id }
    }
  }
`;

type StarKey = "overall" | "cleanliness" | "communication" | "accuracy" | "valueScore" | "respectRules";
type TextKey = "body" | "privateFeedback";

type StarStep = { kind: "star"; key: StarKey; label: string; description?: string; required?: boolean };
type TextStep = { kind: "text"; key: TextKey; label: string; placeholder: string };
type Step = StarStep | TextStep;

const GUEST_STEPS: Step[] = [
  { kind: "star", key: "overall",       label: "Overall Experience",                                          required: true },
  { kind: "star", key: "cleanliness",   label: "Cleanliness",   description: "Was the sauna clean and well-maintained?"     },
  { kind: "star", key: "communication", label: "How was the host's communication?"                                          },
  { kind: "star", key: "accuracy",      label: "Accuracy",      description: "Did the experience match the booking?"        },
  { kind: "star", key: "valueScore",    label: "Value",         description: "Was the sauna experience worth what you paid?" },
  { kind: "text", key: "body",          label: "Anything to share with future guests?", placeholder: "Optional — what stood out?" },
  { kind: "text", key: "privateFeedback", label: "Private note for the host?", placeholder: "Only your host will see this." },
];

const HOST_STEPS: Step[] = [
  { kind: "star", key: "overall",       label: "Overall Experience",                                        required: true },
  { kind: "star", key: "cleanliness",   label: "Cleanliness",   description: "Did the guest leave the sauna clean?"      },
  { kind: "star", key: "communication", label: "How was the guest's communication?"                                       },
  { kind: "star", key: "respectRules",  label: "House rules"                                                              },
  { kind: "text", key: "body",          label: "Anything to share?", placeholder: "Optional — how did the stay go?" },
];

export default function ReviewWizard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const bookingId = parseInt(id!, 10);
  const currentUserId = useCurrentUser();

  const { data, loading, error } = useQuery(GET_BOOKING_FOR_REVIEW, {
    variables: { id: bookingId },
    fetchPolicy: "network-only",
  });
  const [submitReview, { loading: submitting }] = useMutation(SUBMIT_REVIEW, {
    refetchQueries: ["BookingDetail", "MyPendingReviews", "MyWrittenReviews", "MySaunas"],
  });

  const [stepIndex, setStepIndex] = useState(0);
  const [ratings, setRatings] = useState<Partial<Record<StarKey, number>>>({});
  const [body, setBody] = useState("");
  const [privateFeedback, setPrivateFeedback] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (loading) return <Page><p>Loading…</p></Page>;
  if (error)   return <Page><p style={{ color: "#ef4444" }}>{error.message}</p></Page>;

  const b = data?.bookingById;
  if (!b) return <Page><p>Booking not found.</p></Page>;

  const ownerId  = b.saunaBySaunaId?.userByOwnerId?.id;
  const renterId = b.userByRenterId?.id;
  const isHost   = currentUserId === ownerId;
  const isRenter = currentUserId === renterId;
  if (!isHost && !isRenter) {
    return <Page><p style={{ color: "#ef4444" }}>You don't have access to this booking.</p></Page>;
  }

  const role: "guest" | "host" = isHost ? "host" : "guest";
  const stayEnded = new Date(b.endAt) < new Date();
  const alreadyReviewed = (b.reviewsByBookingId?.nodes ?? []).some((r: any) => r.reviewerId === currentUserId);

  if (!stayEnded) {
    return (
      <Page>
        <h2 style={titleStyle}>Not yet</h2>
        <p style={{ color: "#6b7280", marginBottom: 20 }}>You can leave a review once the stay has ended.</p>
        <Link to={`/bookings/${bookingId}`} style={primaryLinkBtn}>Back to booking</Link>
      </Page>
    );
  }

  if (alreadyReviewed && !done) {
    return (
      <Page>
        <h2 style={titleStyle}>Already reviewed</h2>
        <p style={{ color: "#6b7280", marginBottom: 20 }}>You've already submitted a review for this booking.</p>
        <Link to={`/bookings/${bookingId}`} style={primaryLinkBtn}>Back to booking</Link>
      </Page>
    );
  }

  const counterpartName = isHost
    ? nameFor(b.userByRenterId?.userProfileByUserId, "your guest")
    : nameFor(b.saunaBySaunaId?.userByOwnerId?.userProfileByUserId, "your host");

  const steps = role === "guest" ? GUEST_STEPS : HOST_STEPS;
  const isSummary = stepIndex >= steps.length;
  const totalSlots = steps.length + 1;
  const step = steps[stepIndex];

  function handleStarPick(key: StarKey, value: number) {
    setRatings((prev) => ({ ...prev, [key]: value }));
    setTimeout(() => setStepIndex((i) => i + 1), 350);
  }

  function handleAdvance() { setStepIndex((i) => i + 1); }
  function handleBack()    { setStepIndex((i) => Math.max(0, i - 1)); }

  async function handleSubmit() {
    setSubmitError(null);
    try {
      await submitReview({
        variables: {
          bookingId,
          overall: ratings.overall!,
          cleanliness:   ratings.cleanliness   ?? null,
          communication: ratings.communication ?? null,
          accuracy:      role === "guest" ? (ratings.accuracy   ?? null) : null,
          valueScore:    role === "guest" ? (ratings.valueScore ?? null) : null,
          respectRules:  role === "host"  ? (ratings.respectRules ?? null) : null,
          body:           body.trim() || null,
          privateFeedback: (role === "guest" && privateFeedback.trim()) ? privateFeedback.trim() : null,
        },
      });
      setDone(true);
    } catch (err: any) {
      setSubmitError(err?.message ?? "Failed to submit review");
    }
  }

  if (done) {
    return (
      <Page>
        <h2 style={titleStyle}>✓ Review submitted</h2>
        <p style={{ color: "#6b7280", marginBottom: 24, textAlign: "center" }}>
          Thanks for sharing your experience.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button onClick={() => navigate(`/bookings/${bookingId}`)} style={primaryBtn}>Back to booking</button>
          <button onClick={() => navigate("/dashboard")} style={secondaryBtn}>Dashboard</button>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <ProgressDots total={totalSlots} current={stepIndex} />

      {!isSummary && step.kind === "star" && (
        <StarQuestion
          label={step.label}
          description={step.description}
          required={step.required}
          value={ratings[step.key] ?? null}
          onPick={(v) => handleStarPick(step.key, v)}
        />
      )}

      {!isSummary && step.kind === "text" && (
        <TextQuestion
          label={step.label}
          placeholder={step.placeholder}
          value={step.key === "body" ? body : privateFeedback}
          onChange={(v) => step.key === "body" ? setBody(v) : setPrivateFeedback(v)}
          onSkip={handleAdvance}
          onContinue={handleAdvance}
        />
      )}

      {isSummary && (
        <Summary
          ratings={ratings}
          body={body}
          privateFeedback={privateFeedback}
          role={role}
          counterpartName={counterpartName}
          onSubmit={handleSubmit}
          submitting={submitting}
          error={submitError}
        />
      )}

      <p style={disclaimerStyle}>
        Leave an honest review — this will boost your responsiveness score.
      </p>

      <div style={footerStyle}>
        {stepIndex > 0 ? (
          <button onClick={handleBack} style={backBtn}>← Back</button>
        ) : <span />}
        <Link to={`/bookings/${bookingId}`} style={viewBookingLink}>
          View booking details
        </Link>
      </div>
    </Page>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Page({ children }: { children: React.ReactNode }) {
  return (
    <div style={pageStyle}>
      <div style={cardStyle}>{children}</div>
    </div>
  );
}

function StarQuestion({ label, description, required, value, onPick }: {
  label: string; description?: string; required?: boolean; value: number | null; onPick: (v: number) => void;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const display = hovered ?? value ?? 0;

  return (
    <div style={{ textAlign: "center", padding: "28px 0 12px" }}>
      <h2 style={questionStyle}>
        {label}{required && <span style={{ color: "#ef4444" }}> *</span>}
      </h2>
      {description && <p style={stepDescriptionStyle}>{description}</p>}
      <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onPick(n)}
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(null)}
            aria-label={`${n} of 5`}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 56, padding: 0, lineHeight: 1,
              color: n <= display ? "#f59e0b" : "#d1d5db",
              transition: "color 120ms, transform 120ms",
              transform: n <= display && hovered === n ? "scale(1.1)" : "scale(1)",
            }}
          >
            ★
          </button>
        ))}
      </div>
    </div>
  );
}

function TextQuestion({ label, placeholder, value, onChange, onSkip, onContinue }: {
  label: string; placeholder: string; value: string;
  onChange: (v: string) => void; onSkip: () => void; onContinue: () => void;
}) {
  return (
    <div style={{ padding: "20px 0 4px" }}>
      <h2 style={{ ...questionStyle, marginBottom: 16 }}>{label}</h2>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, 1000))}
        rows={5}
        placeholder={placeholder}
        autoFocus
        style={textareaStyle}
      />
      <div style={{ fontSize: 11, color: "#9ca3af", textAlign: "right", marginTop: 4 }}>
        {value.length}/1000
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 16 }}>
        <button type="button" onClick={onSkip} style={skipBtn}>Skip</button>
        <button type="button" onClick={onContinue} style={primaryBtn}>Continue</button>
      </div>
    </div>
  );
}

function Summary({ ratings, body, privateFeedback, role, counterpartName, onSubmit, submitting, error }: {
  ratings: Partial<Record<StarKey, number>>;
  body: string;
  privateFeedback: string;
  role: "guest" | "host";
  counterpartName: string;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
}) {
  const lines: { label: string; value: number | undefined }[] = [
    { label: "Overall Experience", value: ratings.overall },
    { label: "Cleanliness",        value: ratings.cleanliness },
    { label: "Communication",      value: ratings.communication },
    ...(role === "guest"
      ? [{ label: "Accuracy", value: ratings.accuracy }, { label: "Value", value: ratings.valueScore }]
      : [{ label: "House rules", value: ratings.respectRules }]),
  ];

  return (
    <div style={{ padding: "20px 0 4px" }}>
      <h2 style={{ ...questionStyle, marginBottom: 16 }}>Review for {counterpartName}</h2>
      <div style={{ marginBottom: 16 }}>
        {lines.map((l) => (
          <div key={l.label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: 14, borderBottom: "1px solid #f3f4f6" }}>
            <span style={{ color: "#6b7280" }}>{l.label}</span>
            <span style={{ color: l.value ? "#f59e0b" : "#d1d5db", fontSize: 16 }}>
              {l.value ? "★".repeat(l.value) + "☆".repeat(5 - l.value) : "—"}
            </span>
          </div>
        ))}
      </div>
      {body && (
        <div style={{ marginBottom: 12, padding: 12, background: "#f9fafb", borderRadius: 6 }}>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Public review
          </div>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>{body}</p>
        </div>
      )}
      {role === "guest" && privateFeedback && (
        <div style={{ marginBottom: 12, padding: 12, background: "#fef9c3", borderRadius: 6 }}>
          <div style={{ fontSize: 11, color: "#92400e", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Private note (host only)
          </div>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>{privateFeedback}</p>
        </div>
      )}
      {error && <p style={{ color: "#ef4444", fontSize: 13, textAlign: "center", margin: "8px 0" }}>{error}</p>}
      <button onClick={onSubmit} disabled={submitting || !ratings.overall} style={{ ...primaryBtn, width: "100%", marginTop: 8 }}>
        {submitting ? "Submitting…" : "Submit review"}
      </button>
    </div>
  );
}

function ProgressDots({ total, current }: { total: number; current: number }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 4 }}>
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          style={{
            width: 8, height: 8, borderRadius: 4,
            background: i <= current ? "#2563eb" : "#e5e7eb",
            transition: "background 200ms",
          }}
        />
      ))}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  minHeight: "calc(100vh - 56px)",
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "60px 24px",
  background: "#fafafa",
};

const cardStyle: React.CSSProperties = {
  width: "100%", maxWidth: 480,
  background: "#fff", borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: "28px 32px 24px",
};

const titleStyle: React.CSSProperties = {
  fontSize: 22, fontWeight: 600, margin: "8px 0 12px", textAlign: "center",
};

const questionStyle: React.CSSProperties = {
  fontSize: 22, fontWeight: 600, margin: "12px 0 28px", textAlign: "center",
};

const stepDescriptionStyle: React.CSSProperties = {
  fontSize: 14, color: "#6b7280",
  textAlign: "center", margin: "-20px 0 24px",
  lineHeight: 1.4,
};

const textareaStyle: React.CSSProperties = {
  width: "100%", border: "1px solid #d1d5db", borderRadius: 6,
  padding: "10px 12px", fontSize: 14, resize: "vertical",
  fontFamily: "inherit", boxSizing: "border-box", minHeight: 110,
};

const primaryBtn: React.CSSProperties = {
  padding: "10px 24px", background: "#2563eb",
  color: "#fff", border: "none", borderRadius: 6,
  cursor: "pointer", fontSize: 14, fontWeight: 600,
};

const primaryLinkBtn: React.CSSProperties = {
  ...primaryBtn,
  display: "inline-block",
  textDecoration: "none",
};

const secondaryBtn: React.CSSProperties = {
  padding: "10px 24px", background: "#fff",
  color: "#374151", border: "1px solid #d1d5db", borderRadius: 6,
  cursor: "pointer", fontSize: 14, fontWeight: 500,
};

const skipBtn: React.CSSProperties = {
  padding: "10px 24px", background: "#fff",
  color: "#6b7280", border: "1px solid #d1d5db", borderRadius: 6,
  cursor: "pointer", fontSize: 14,
};

const backBtn: React.CSSProperties = {
  background: "none", border: "none", cursor: "pointer",
  color: "#6b7280", fontSize: 13, padding: 0,
};

const disclaimerStyle: React.CSSProperties = {
  fontSize: 12, color: "#9ca3af",
  textAlign: "center", marginTop: 28, marginBottom: 12,
  lineHeight: 1.5,
};

const footerStyle: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
};

const viewBookingLink: React.CSSProperties = {
  fontSize: 13, color: "#2563eb", textDecoration: "none", marginLeft: "auto",
};
